import { createSlice } from '@reduxjs/toolkit';
//
import { dispatch } from '../store';
// slice
import { updateError } from './user';
// utils
import { callApi } from '../../utils/apiCall';
import { hasFeatureAccess } from '../../utils/featurePermission';
import { CHECKLIST_COMPLETION_STATE } from '../../constants/tasksV2';

// -----------------------------------------------

const initialState = {
    isLoading: false,
    isCurrentTaskLoading: false,
    tasks: null,
    currentTask: null,
    summary: null,
    activity: null,
    comments: null,
};

const slice = createSlice({
    name: 'task',
    initialState,
    reducers: {
        reset(state) {
            Object.assign(state, initialState);
        },
        startLoading(state) {
            state.isLoading = true;
        },
        startCurrentTaskLoading(state) {
            state.isCurrentTaskLoading = true;
        },
        setTasks(state, action) {
            state.isLoading = false;
            state.tasks = action.payload;
        },
        setCurrentTask(state, action) {
            state.isLoading = false;
            state.isCurrentTaskLoading = false;
            state.currentTask = action.payload;
        },
        setSummary(state, action) {
            state.isLoading = false;
            state.summary = action.payload;
        },
        setActivity(state, action) {
            state.isLoading = false;
            state.activity = action.payload;
        },
        setComments(state, action) {
            state.isLoading = false;
            state.comments = action.payload;
        },
    }
});

export default slice.reducer;

export const { reset, startLoading, startCurrentTaskLoading, setTasks, setCurrentTask, setSummary, setActivity, setComments } = slice.actions;

export function getTasks(practiceId, params) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            dispatch(slice.actions.startLoading());
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/?${params}`);
            if (response.status === 200) {
                dispatch(slice.actions.setTasks(response.data));
            } else {
                dispatch(updateError({ success: false, message: response?.data?.detail || response?.data?.message || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function getTask(practiceId, taskId) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            dispatch(slice.actions.startCurrentTaskLoading());
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/`);
            if (response.status === 200) {
                dispatch(slice.actions.setCurrentTask(response.data));
                return { ok: true };
            }
            if (response.status === 404) {
                dispatch(slice.actions.setCurrentTask(null));
                return { notFound: true };
            }
            dispatch(slice.actions.setCurrentTask(null));
            dispatch(updateError({ success: false, message: response?.data?.detail || response?.data?.message || 'Something went wrong' }));
            return { error: true };
        } catch (err) {
            dispatch(slice.actions.setCurrentTask(null));
            dispatch(updateError({ success: false, message: err.message || 'Something went wrong' }));
            return { networkError: true };
        }
    };
}

export function createTask(practiceId, data, handleSuccess) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/`, 'post', data);
            if (response.status === 201) {
                dispatch(updateError({ success: true, message: 'Task created successfully' }));
                handleSuccess?.({ success: true });
            } else {
                const message = response?.data?.detail || response?.data?.message || 'Something went wrong';
                dispatch(updateError({ success: false, message }));
                handleSuccess?.({ success: false, message });
            }
        } catch (error) {
            const message = error.message || 'Something went wrong';
            dispatch(updateError({ success: false, message }));
            handleSuccess?.({ success: false, message });
        }
    };
}

export function updateTask(practiceId, taskId, data, handleSuccess) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/`, 'patch', data);
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: 'Task updated successfully' }));
                handleSuccess({ success: true });
            } else {
                dispatch(updateError({ success: false, message: response?.data?.detail || response?.data?.message || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function completeTask(practiceId, taskId, handleSuccess) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/complete/`, 'post');
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: 'Task completed successfully' }));
                handleSuccess({ success: true });
            } else {
                const detail = response.data?.detail
                    || response.data?.non_field_errors?.[0]
                    || (typeof response.data === 'string' ? response.data : null)
                    || 'Could not complete task';
                dispatch(updateError({ success: false, message: detail }));
                handleSuccess({ success: false, message: detail });
            }
        } catch (error) {
            const detail = error.response?.data?.detail || error.message || 'Something went wrong';
            dispatch(updateError({ success: false, message: detail }));
            handleSuccess({ success: false, message: detail });
        }
    };
}

export function reopenTask(practiceId, taskId, handleSuccess) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/reopen/`, 'post');
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: 'Task reopened successfully' }));
                if (handleSuccess) handleSuccess({ success: true });
            } else {
                dispatch(updateError({ success: false, message: response?.data?.detail || response?.data?.message || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function archiveTask(practiceId, taskId, handleSuccess) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/`, 'delete');
            if (response.status === 204 || response.status === 200) {
                dispatch(updateError({ success: true, message: 'Task archived successfully' }));
                if (handleSuccess) handleSuccess({ success: true });
            } else {
                dispatch(updateError({ success: false, message: response?.data?.detail || response?.data?.message || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function deleteTask(practiceId, taskId, handleSuccess) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/delete/`, 'post');
            if (response.status === 204 || response.status === 200) {
                dispatch(updateError({ success: true, message: 'Task deleted' }));
                if (handleSuccess) handleSuccess({ success: true });
            } else {
                const detail = response?.data?.detail || response?.data?.message || 'Could not delete task';
                dispatch(updateError({ success: false, message: detail }));
                if (handleSuccess) handleSuccess({ success: false, message: detail });
            }
        } catch (error) {
            const detail = error.message || 'Something went wrong';
            dispatch(updateError({ success: false, message: detail }));
            if (handleSuccess) handleSuccess({ success: false, message: detail });
        }
    };
}

export function restoreTask(practiceId, taskId, handleSuccess) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/restore/`, 'post');
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: 'Task restored' }));
                if (handleSuccess) handleSuccess({ success: true, data: response.data });
            } else if (response.status === 410) {
                dispatch(updateError({ success: false, message: 'Task cannot be restored — past 30-day window' }));
                if (handleSuccess) handleSuccess({ success: false, gone: true });
            } else {
                const detail = response?.data?.detail || response?.data?.message || 'Could not restore task';
                dispatch(updateError({ success: false, message: detail }));
                if (handleSuccess) handleSuccess({ success: false, message: detail });
            }
        } catch (error) {
            const detail = error.message || 'Something went wrong';
            dispatch(updateError({ success: false, message: detail }));
            if (handleSuccess) handleSuccess({ success: false, message: detail });
        }
    };
}

export function getTaskSummary(practiceId) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/my-summary/`);
            if (response.status === 200) {
                dispatch(slice.actions.setSummary(response.data));
            } else {
                dispatch(updateError({ success: false, message: response?.data?.detail || response?.data?.message || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

// BE CONTRACT — completeChecklistItem
// POST /api/v1/practices/{p}/tasks/{taskId}/checklist/{itemId}/complete/
//
// Canonical body shape — mirrors ChecklistItemRow.jsx::useCompletionApi.postState (lines 479-490):
//   { completion_state: 'YES' }                                        — all item types
//   { completion_state: 'YES', response_payload: { resolved_action } } — LEAD_NEXT_ACTION only
//   { completion_state: 'SKIPPED', response_payload: { source: 'upnext_skip' } } — skip via Up Next card
//     (bulk skip via FloatingBulkActionBar uses { answer: 'manual' } instead — distinguishable in audit log)
//
// LEAD_NEXT_ACTION items REQUIRE resolved_action nested inside response_payload.
// Missing resolved_action → 400 {"detail": "resolved_action is required for LEAD_NEXT_ACTION cells."}
//
// Valid resolved_action keys — driven by NextActionPanel.jsx OUTCOMES (BE has NO whitelist;
// any string is accepted, but these are the FE-defined canonical values):
//   treatment_start     — user intends to start treatment
//   clincheck           — ClinCheck review scheduled
//   follow_up           — follow-up contact recorded
//   treatment_complete  — treatment marked complete
//   archive             — lead archived (also fires V1 ArchivedLeadDashBoard thunk)
//
// Callers: AttendedGuidedChecklist.jsx handlePanelComplete + AttendedGuidedChecklistV2.jsx.
// NextActionPanel sets completedItem.resolvedAction which the orchestrator maps to
// { resolved_action } before calling this thunk as the `payload` arg.
//
// All other item types (EMAIL_SENT, PHONE_CALLED, etc.) → null payload → body = { completion_state }.
//
// completionState defaults to 'YES'. Pass 'SKIPPED' for the Up Next "Skip for now" flow.
// Success toast is suppressed for SKIPPED — the UI advances silently.
//
// handleSuccess is always called: { success: true } on 200, { success: false } on
// error / 4xx / 5xx — callers can reset optimistic state or show a retry affordance.
export function completeChecklistItem(practiceId, taskId, itemId, handleSuccess, payload = null, completionState = CHECKLIST_COMPLETION_STATE.YES) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            // Build body per canonical BE contract (ChecklistItemRow.jsx::useCompletionApi.postState)
            const body = { completion_state: completionState };
            if (payload) body.response_payload = payload;
            const response = await callApi(
                `/api/v1/practices/${practiceId}/tasks/${taskId}/checklist/${itemId}/complete/`,
                'post',
                body,
            );
            if (response.status === 200) {
                // Suppress toast for SKIPPED — callers advance UI silently.
                if (completionState !== CHECKLIST_COMPLETION_STATE.SKIPPED) {
                    dispatch(updateError({ success: true, message: 'Checklist item completed' }));
                }
                if (handleSuccess) handleSuccess({ success: true });
            } else {
                dispatch(updateError({ success: false, message: response?.data?.detail || response?.data?.message || 'Something went wrong' }));
                if (handleSuccess) handleSuccess({ success: false });
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
            if (handleSuccess) handleSuccess({ success: false });
        }
    };
}

export function reopenChecklistItem(practiceId, taskId, itemId, handleSuccess) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/checklist/${itemId}/reopen/`, 'post');
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: 'Checklist item reopened' }));
                if (handleSuccess) handleSuccess({ success: true });
            } else {
                dispatch(updateError({ success: false, message: response?.data?.detail || response?.data?.message || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function addChecklistItem(practiceId, taskId, data, handleSuccess) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/checklist/`, 'post', data);
            if (response.status === 201) {
                dispatch(updateError({ success: true, message: 'Checklist item added' }));
                if (handleSuccess) handleSuccess({ success: true });
            } else {
                dispatch(updateError({ success: false, message: response?.data?.detail || response?.data?.message || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function addComment(practiceId, taskId, data, handleSuccess) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/comments/`, 'post', data);
            if (response.status === 201) {
                dispatch(updateError({ success: true, message: 'Comment added' }));
                if (handleSuccess) handleSuccess({ success: true });
            } else {
                dispatch(updateError({ success: false, message: response?.data?.detail || response?.data?.message || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function getActivity(practiceId, taskId) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/activity/`);
            if (response.status === 200) {
                dispatch(slice.actions.setActivity(response.data));
            } else {
                dispatch(updateError({ success: false, message: response?.data?.detail || response?.data?.message || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function getComments(practiceId, taskId) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/comments/`);
            if (response.status === 200) {
                dispatch(slice.actions.setComments(response.data));
            } else {
                dispatch(updateError({ success: false, message: response?.data?.detail || response?.data?.message || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function updateComment(practiceId, taskId, commentId, data, handleSuccess) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/comments/${commentId}/`, 'patch', data);
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: 'Comment updated' }));
                if (handleSuccess) handleSuccess({ success: true });
            } else {
                dispatch(updateError({ success: false, message: response?.data?.detail || response?.data?.message || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function deleteComment(practiceId, taskId, commentId, handleSuccess) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/comments/${commentId}/`, 'delete');
            if (response.status === 204 || response.status === 200) {
                dispatch(updateError({ success: true, message: 'Comment deleted' }));
                if (handleSuccess) handleSuccess({ success: true });
            } else {
                dispatch(updateError({ success: false, message: response?.data?.detail || response?.data?.message || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function pauseTask(practiceId, taskId, { resumeAt }, onSuccess) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/pause/`, 'post', { resume_at: resumeAt }); // eslint-disable-line camelcase
            if (response.status === 200) {
                if (onSuccess) onSuccess(response.data);
            } else {
                dispatch(updateError({ success: false, message: response?.data?.detail || response?.data?.message || 'Could not pause task' }));
            }
        } catch (err) {
            dispatch(updateError({ success: false, message: err.message || 'Could not pause task' }));
        }
    };
}

export function resumeTask(practiceId, taskId, onSuccess) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/resume/`, 'post');
            if (response.status === 200) {
                if (onSuccess) onSuccess(response.data);
            } else {
                dispatch(updateError({ success: false, message: response?.data?.detail || response?.data?.message || 'Could not resume task' }));
            }
        } catch (err) {
            dispatch(updateError({ success: false, message: err.message || 'Could not resume task' }));
        }
    };
}

export function createFromTemplate(practiceId, data, handleSuccess) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/from-template/`, 'post', data);
            if (response.status === 201) {
                dispatch(updateError({ success: true, message: 'Task created from template' }));
                if (handleSuccess) handleSuccess({ success: true });
            } else {
                dispatch(updateError({ success: false, message: response?.data?.detail || response?.data?.message || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}
